import type { AppEnvironment } from './types';
import { IP4CMS_PAGE_COPY } from './page-copy-ip4cms';

/**
 * SBMDC member portal — STAGING, single-tenant (NOT SSO).
 *
 * Same backend as the ip4cms vertical (core-api / api .ip4cms-staging.ip4x.systems
 * — the same core ip4cms uses), but pinned to the `sbm` tenant
 * (247a2ad3-d451-4b2f-a325-ce8cd0d951c9) via its member domain. Served from
 * `dist/sbmdc-staging` behind Caddy at `sbmdc.member.ip4cms-staging.ip4x.systems`
 * and wrapped as a dedicated native app (com.sudosky.sbmdc).
 *
 * Unlike `ip4cms-staging` (SSO root scope e50b733a, community switcher), this is
 * a direct single-tenant build: `rootTenantId: ''` so there's no SSO scope, no
 * community switcher and no "join community" — launch resolves the pinned
 * `defaultDomain` straight to the SBM tenant + its branding. Core must include
 * `member.ip4cms-staging.ip4x.systems` in BASE_DOMAINS (it already does, for the
 * ip4cmsadmin member portal) so `{slug}` is extracted + on-demand TLS approved.
 */
export const env: AppEnvironment = {
  production: true,
  buildMode: 'sbmdc-staging',
  apiBaseUrl: 'https://api.ip4cms-staging.ip4x.systems/v1',
  coreApiUrl: 'https://core-api.ip4cms-staging.ip4x.systems/v1',
  // Shared "DCApp" member app template (code `dcapp`) owned by the ip4cms tenant.
  // SBM + Stellenbosch both render from this one baseline template.
  appTemplateId: 'fbb54d26-d0e8-4f98-99cf-7ca377ab0a72',
  push: { enabled: true },
  // 'tenant' (NOT 'core'): authenticate directly against the tenant API
  // (/auth/login) instead of the Core portal-auth SSO gateway. 'core' would
  // return an SSO session + linked-tenants list (the community switcher) even
  // with no root scope — i.e. single sign-on behaviour, which this standalone
  // single-tenant app must not have. coreApiUrl is still used for boot
  // resolve-domain (branding/region), not for login.
  authMode: 'tenant',
  portalOwnerType: 'member',
  loadDomainConfig: true,
  // Single-tenant: defaultDomain pins the app to the SBM tenant so launch
  // resolves its branding directly (on the native shell / localhost, per
  // prefersConfiguredDomain). Empty rootTenantId = no SSO scope.
  defaultDomain: 'sbmdc.member.ip4cms-staging.ip4x.systems',
  rootTenantId: '',
  portalBaseUrl: 'https://sbmdc.member.ip4cms-staging.ip4x.systems',
  appName: 'Saldanha Bay',
  version: '1.0.0',
  appShell: 'classic',
  keyboardShift: true,
  welcomeContent: {
    eyebrow: 'Saldanha Bay Municipality',
    title: 'Your community,\nin your pocket.',
    subtitle: 'Access, services and your home, all in one place.',
  },
  communitySelectContent: {
    title: 'Choose your community',
    subtitle: 'Search for the community you live in to begin registration.',
  },
  signupTermsUrl: 'https://www.glovent.co.za/terms',
  // SBM refers to its suppliers as "providers" — override just that hero copy
  // (the shared IP4CMS_PAGE_COPY is also used by the ip4cms builds).
  pageCopy: {
    ...IP4CMS_PAGE_COPY,
    suppliers: {
      eyebrow: 'Local services',
      title: 'Providers.',
      subtitle: 'Browse approved providers by category.',
      emptyTitle: 'No providers.',
      emptyDescription: "Your community hasn't published any providers yet.",
    },
  },
  home: {
    widgetMode: true,
    widgets: [
      { id: 'account', type: 'account', title: 'Account', order: 1, enabled: true, requiresModule: 'financials' },
      { id: 'support', type: 'support', title: 'Helpdesk', order: 2, enabled: true, requiresModule: 'support' },
      { id: 'events', type: 'events', title: 'Upcoming events', order: 3, enabled: true, requiresModule: 'events' },
      { id: 'panic', type: 'panic', title: 'Emergency', order: 4, enabled: true, requiresModule: 'panic' },
      { id: 'utilities', type: 'utilities', title: 'Utilities', order: 5, enabled: true, requiresModule: 'utilities' },
      { id: 'access', type: 'access', title: 'Visitor access', order: 6, enabled: true, requiresModule: 'access_management' },
      { id: 'news', type: 'news', title: 'News', order: 7, enabled: true, requiresModule: 'newsstand' },
    ],
  },
};
